import type { PlayerService, StreamingService } from "../types";
import type { PlaybackState } from "./types";

export interface PlayerDevice {
  id: string;
  active: boolean;
  name: string;
  type: string;
  volume: number;
  supportsVolume: boolean;
}

export const defaultPlaybackState: () => PlaybackState = () => ({
  currentTrack: null,
  isPaused: true,
  repeatMode: "off",
  isShuffle: false,
  progressMs: 0,
  isLoading: false,
  originalEvent: null,
  volume: 1,
  supportsVolume: true,
});

export abstract class ServicePlayer {
  abstract service: PlayerService;

  playbackState: PlaybackState;

  constructor() {
    this.playbackState = defaultPlaybackState();
  }

  abstract supportsDevices: boolean;

  abstract init(): Promise<void>;
  abstract pause(): Promise<void>;
  abstract resume(): Promise<void>;
  abstract seek(positionMs: number): Promise<void>;
  abstract nextTrack(): Promise<void>;
  abstract previousTrack(): Promise<void>;
  abstract playPlaylist(
    playlistId: string,
    options?: { position?: number; deviceId?: string }
  ): Promise<void>;
  abstract playAlbum(
    albumId: string,
    options?: { position?: number; deviceId?: string }
  ): Promise<void>;
  abstract playTrack(
    trackId: string,
    options?: { positionMs?: number; deviceId?: string }
  ): Promise<void>;
  abstract playTracks(
    trackIds: string[],
    options?: { position?: number; deviceId?: string }
  ): Promise<void>;
  abstract getDevices(): Promise<PlayerDevice[]>;
  abstract setDevice(deviceId: string): Promise<void>;
  abstract togglePlayback(shuffle: boolean): Promise<void>; // TODO: naming of this function doesn't make sense
  abstract setRepeatMode(mode: "off" | "context" | "track"): Promise<void>;
  abstract enqueueTracks(tracks: string[]): Promise<void>;
  /**
   * Set the volume of the player
   * @param volume Volume to set, between 0 and 1
   */
  abstract setVolume(volume: number): Promise<void>;

  dispatchState(): void {
    document.dispatchEvent(
      new CustomEvent("umg:player:playbackStateChanged", {
        // deepcopy the state before dispatch
        detail: JSON.parse(JSON.stringify(this.playbackState)),
      })
    );
  }
}
